import { useLoadableState } from '@carvana/merch-react-hooks';
import { datadogRum } from '@datadog/browser-rum';
import classNames from 'classnames';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { lightLoadingOverlay } from '@/common/LoadingOverlay';
import Sections from '~/constants/sections';
import type { SocialProof as SocialProofContract } from '~/contracts/vdp';
import { useToggleFavorite } from '~/hooks/vdp/favorites';
import {
  getVehicleIdAndStockNumber,
  isCurrentVehicleFavorited
} from '~/selectors';
import { getSocialProof } from '~/services/vdp';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import generateErrorLogger from '~/utilities/errorHandlers';
import Saved from './Saved';
import { Section, InnerWrapper } from './SocialProof.styles';
import Views from './Views';

const renderThreshold = 10;

const SocialProof = () => {
  const { data, isLoaded, setData, setError } =
    useLoadableState<SocialProofContract>({
      numberTimesFavorited: 0,
      numberTimesViewed: 0,
      isHot: false
    });
  const { stockNumber, vehicleId } = useSelector(getVehicleIdAndStockNumber);
  const isFavorited = useSelector(isCurrentVehicleFavorited);
  const toggleFavorite = useToggleFavorite();

  useEffect(() => {
    getSocialProof(vehicleId)
      .then(response => {
        setData(response);
        datadogRum.addAction('social-proof-rendered', {
          // Datadog was not allowing filtering by custom booleans
          rendered: response.numberTimesViewed >= renderThreshold ? 1 : 0
        });
      })
      .catch(
        generateErrorLogger(
          'Get social proof',
          {},
          {
            callback: error => {
              setError(error);
              datadogRum.addAction('social-proof-rendered', {
                rendered: 0
              });
            }
          }
        )
      );
  }, [setData, setError, isFavorited, vehicleId]);

  const { numberTimesFavorited, numberTimesViewed, isHot } = data;

  const shouldHide = isLoaded && numberTimesViewed < renderThreshold;

  return shouldHide ? null : (
    <Section
      className={classNames('bg-grey-1', {
        [lightLoadingOverlay]: !isLoaded
      })}
      id={Sections.SocialProof}
    >
      <InnerWrapper>
        <Saved
          handleClick={() => toggleFavorite({ stockNumber, vehicleId })}
          isFavorited={isFavorited}
          count={numberTimesFavorited}
        />
        <Views count={numberTimesViewed} isHot={isHot} />
      </InnerWrapper>
    </Section>
  );
};

export default withErrorBoundary(
  SocialProof,
  undefined,
  undefined,
  Sections.SocialProof
);
